import styled, { css } from "styled-components";
import { mobileOnly } from "_/style/media";

import { FormField } from "_/components/form-field";
import { Button } from "_/components/button";
import { TextField } from "_/components/text-field";
import { Icon } from "_/components/icon";
import { Checkbox } from "_/components/checkbox";
import { numericFontStyles, palette } from "_/style";

import { LogLevel } from "_/data/machines";

export function levelColor(level: LogLevel): string {
  switch (level) {
    case "error":
      return palette.tomatoRed;
    case "warn":
      return palette.pencilYellow;
    case "info":
      return palette.cornflowerBlue;
    case "debug":
    default:
      return palette.ironGrey;
  }
}

export const Wrapper = styled.div`
  margin: 0 2.5rem 3.75rem;

  ${mobileOnly`
    margin: 0 1rem 3.75rem;
  `}
`;

export const Content = styled.div`
  margin-top: 1.375rem;
`;

export const FilterBar = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 1rem 0 0;
  align-items: center;

  ${mobileOnly`
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  `}
`;

export const FilterBarItem = styled.div`
  display: flex;
  place-items: center;
  gap: 0.5rem;

  ${FormField} {
    margin: 0;
    width: auto;
  }
`;

export const GrowingFilterBarItem = styled(FilterBarItem)`
  flex-grow: 1;
  ${FormField} {
    flex-grow: 1;
  }
`;

// Keeps the range select and the buttons after it on one line, even on
// narrow screens where the rest of the filter bar stacks vertically.
export const RangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  ${mobileOnly`
    gap: 0.75rem;

    & > ${FilterBarItem} {
      flex-grow: 1;
    }

    & > ${FilterBarItem} ${FormField} {
      flex-grow: 1;
    }
  `}
`;

export const CopyStatusIcon = styled(Icon)<{ $copied: boolean }>`
  color: ${(p) => (p.$copied ? p.theme.color.palette.kellyGreen : "inherit")};
`;

export const Label = styled.label`
  color: ${(p) => p.theme.text.muted};
  font-size: 0.875rem;
`;

export const RawJson = styled.pre`
  font-family: monospace;
  font-size: 0.75rem;
`;

export const LogEventCode = styled.code`
  font-family: monospace;
  display: inline;
`;

export const FilterLink = styled.a`
  color: ${(p) => p.theme.text.default};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export const ClearButton = styled(Button)`
  padding: 0.25rem;
  font-size: 10px;
  outline: 0;
  margin-right: -4px;
  color: ${(p) => p.theme.text.muted};
  &:hover:enabled {
    color: ${(p) => p.theme.text.default};
  }
`;

/**
 * Custom TD CSS for logs that reduces vertical space between rows so we can
 * show many more log lines on a page without scrolling.  Also ensures that
 * number columns look better.
 * */
export const customCellCss = css`
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  min-height: 1rem;

  /* Align numbers shown in log cells. */
  ${numericFontStyles}
`;

export const LevelIndicator = styled.span<{ $level: LogLevel }>`
  display: inline-block;
  width: 0.25rem;
  height: 1rem;
  background-color: ${(p) => levelColor(p.$level)};
`;

export const LevelOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const LevelCell = styled.div`
  display: flex;
  padding-left: 0.875rem;
`;

export const LogEventDataLine = styled.div`
  display: flex;
`;

export const GrowingTextField = styled(TextField)`
  flex-grow: 1;
  min-width: 0;
  max-width: 100%;
  width: 100%;
`;

export const SeeMoreLink = styled.a`
  color: ${(p) => p.theme.color.brand.blue};
  text-decoration: underline;
`;

export const ChartSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.375rem;
`;

export const ChartHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem 1.5rem;
`;

export const QueryRange = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${(p) => p.theme.text.muted};
  font-size: 0.875rem;
`;

// Shows the "UTC" label before its checkbox rather than after it, set apart
// from the date/time inputs.
export const UtcCheckbox = styled(Checkbox)`
  flex-direction: row-reverse;
  margin-left: 0.75rem;
`;

export const RangeInput = styled.input`
  color-scheme: ${(p) => p.theme.input.colorScheme};
  color: ${(p) => p.theme.input.default.text};
  background-color: ${(p) => p.theme.input.default.background};
  border: 1px solid ${(p) => p.theme.input.default.border};
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font: inherit;
  cursor: pointer;
  ${numericFontStyles}

  &:hover {
    border-color: ${(p) => p.theme.input.hover.border};
  }

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.input.focused.border};
  }
`;

export const LevelCounts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
`;

export const LevelCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${(p) => p.theme.text.muted};
`;

export const LevelCountValue = styled.span`
  color: ${(p) => p.theme.text.default};
  font-size: 1.125rem;
  ${numericFontStyles}
`;

export const ChartWrapper = styled.div`
  height: 10rem;
  position: relative;
`;

export const TruncatedNotice = styled.div`
  color: ${(p) => p.theme.text.muted};
  font-size: 0.75rem;
`;

export const TooltipContent = styled.div`
  background: ${(p) => p.theme.color.palette.satinBlack};
  border: 1px solid ${(p) => p.theme.color.palette.graniteBlack};
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  color: ${(p) => p.theme.text.default};
  user-select: none;
`;

export const TooltipTime = styled.div`
  margin-bottom: 0.25rem;
  color: ${(p) => p.theme.color.palette.spanishGrey};
  white-space: nowrap;
`;

export const TooltipRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-variant-numeric: tabular-nums;
`;
